import { useState, useMemo, useCallback } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import SwitchButton from 'LuxKit/Switch/SwitchButton'

import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import ExpandableObjectView from '../ExpandableObjectView'
import { useChannelMarkupEvaluationEnabled, setChannelMarkupEvaluationMode } from 'components/App/ChannelMarkupEvaluation/useChannelMarkupEvaluationEnabled'
import selectChannelMarkup from 'lib/channelMarkup/selectChannelMarkup'

function PromoTab() {
  const intl = useIntl()
  const [showChannelMarkupEvaluationToggle, setShowChannelMarkupEvaluationToggle] = useState(true)

  const marketingState = useAppSelector(state => state.marketing)
  const voucherState = useMemo(() => ({
    vouchers: marketingState.vouchers,
    voucherConditionResults: marketingState.voucherConditionResults,
    allVouchers: marketingState.allVouchers,
    allVouchersStatus: marketingState.allVouchersStatus,
    isFetchingAllVouchers: marketingState.isFetchingAllVouchers,
    fetchingVouchersError: marketingState.fetchingVouchersError,
  }), [
    marketingState.allVouchers,
    marketingState.allVouchersStatus,
    marketingState.fetchingVouchersError,
    marketingState.isFetchingAllVouchers,
    marketingState.voucherConditionResults,
    marketingState.vouchers,
  ])
  const channelMarkupState = useMemo(() => ({
    channelMarkup: marketingState.channelMarkup,
    fetching: marketingState.fetching,
  }), [marketingState.channelMarkup, marketingState.fetching])
  const clientInitState = useMemo(() => ({
    requestedAt: marketingState.clientInitConfigRequestedAt,
    response: marketingState.clientInitConfig,
  }), [marketingState.clientInitConfig, marketingState.clientInitConfigRequestedAt])
  const search = window.location.search
  const channelMarkupV2State = useMemo(() => {
    if (config.channelMarkupEvaluationMode !== 'v2') {
      return undefined
    }
    const channelMarkupConfig = marketingState.clientInitConfig?.channelMarkupInitConfig
    if (!channelMarkupConfig) {
      return undefined
    }

    return selectChannelMarkup(channelMarkupConfig, search)
  }, [marketingState.clientInitConfig, search])
  const channelMarkupConfigState = useMemo(() => ({
    enabled: config.ENABLE_CHANNEL_MARKUP,
    evaluationMode: config.channelMarkupEvaluationMode,
    enabledForLPP: config.ENABLE_CHANNEL_MARKUP_FOR_LPP,
    enabledForLPC: config.ENABLE_CHANNEL_MARKUP_FOR_LPC,
    enabledForMemberPrice: config.ENABLE_CHANNEL_MARKUP_FOR_MEMBER_PRICE,
  }), [])
  const channelMarkupEvaluationEnabled = useChannelMarkupEvaluationEnabled()
  const handleChannelMarkupEvaluationToggleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setShowChannelMarkupEvaluationToggle(event.target.checked)
  }, [])

  const toggleChannelMarkupEvaluationMode = () => {
    setChannelMarkupEvaluationMode(!channelMarkupEvaluationEnabled)
  }

  const heading = intl.formatMessage({
    id: 'temp_dev_panel_promo_heading',
    defaultMessage: 'Promo',
    description: 'Heading for the promo state view in the customer portal developer tools panel',
  })
  const description = intl.formatMessage({
    id: 'temp_dev_panel_promo_description',
    defaultMessage: 'Promo service state grouped by promo, voucher, and channel-markup data.',
    description: 'Description explaining the promo service state view in the customer portal developer tools panel',
  })
  const promoHeading = intl.formatMessage({
    id: 'temp_dev_panel_promo_section_promo_heading',
    defaultMessage: 'Promo',
    description: 'Section heading for promo configuration state in the customer portal developer tools panel',
  })
  const voucherHeading = intl.formatMessage({
    id: 'temp_dev_panel_promo_section_voucher_heading',
    defaultMessage: 'Voucher',
    description: 'Section heading for voucher state in the customer portal developer tools panel',
  })
  const channelMarkupHeading = intl.formatMessage({
    id: 'temp_dev_panel_promo_section_channel_markup_heading',
    defaultMessage: 'Channel-markup',
    description: 'Section heading for channel markup state in the customer portal developer tools panel',
  })
  const clientInitHeading = intl.formatMessage({
    id: 'temp_dev_panel_promo_section_client_init_heading',
    defaultMessage: 'Client init',
    description: 'Section heading for the promo client-init response and request time in the customer portal developer tools panel',
  })
  const channelMarkupEvaluationToggleLabel = intl.formatMessage({
    id: 'temp_dev_panel_promo_section_channel_markup_evaluation_toggle_label',
    defaultMessage: 'Show channel markup evaluation toggle',
    description: 'Label for the switch that shows or hides the channel markup evaluation debug toggle in the promo dev panel',
  })

  return <Pane padding={20}>
    <VerticalSpacer gap={20}>
      <div>
        <Heading variant="heading5">{heading}</Heading>
        <div>{description}</div>
        <div>
          <SwitchButton
            checked={showChannelMarkupEvaluationToggle}
            onChange={handleChannelMarkupEvaluationToggleChange}
          >
            {channelMarkupEvaluationToggleLabel}
          </SwitchButton>
        </div>
      </div>

      <div>
        <Heading variant="heading6">{promoHeading}</Heading>
        <ExpandableObjectView value={marketingState.promoDisplayConfigs} initialExpanded />
      </div>

      <div>
        <Heading variant="heading6">{voucherHeading}</Heading>
        <ExpandableObjectView value={voucherState} initialExpanded />
      </div>

      <div>
        <Heading variant="heading6">{clientInitHeading}</Heading>
        <ExpandableObjectView value={clientInitState} initialExpanded />
      </div>

      <div>
        <Heading variant="heading6">{channelMarkupHeading}</Heading>
        {showChannelMarkupEvaluationToggle && <TextButton kind="primary" onClick={toggleChannelMarkupEvaluationMode}>
          {channelMarkupEvaluationEnabled ? 'Hide channel markup evaluations' : 'Show channel markup evaluations'}
        </TextButton>}
        <ExpandableObjectView value={channelMarkupConfigState} initialExpanded />
        <ExpandableObjectView value={channelMarkupV2State} initialExpanded />
        <ExpandableObjectView value={channelMarkupState} initialExpanded />
      </div>
    </VerticalSpacer>
  </Pane>
}

export default PromoTab
